import { Action } from 'redux';
import {
    Contact,
    ContactsOrderBy,
    ContactPattern,
    SuppressedStatus,
    ContactStatus,
    UnsubscribedOverview,
    ContactInfo,
    UnsubscribedContactsOrderBy,
    ContactUnsubscribeReason,
} from '@dev/ee-api/ee-api-models';

import { ExportContactsUnsubscribedParams, ExportSuppressionsParams } from '@api/services/ElasticApi';

export type Statuses = 'Transactional' | 'Bounced' | 'Unsubscribed' | 'Abuse' | 'Inactive' | 'Stale';
export type OrderByOptions = 'DateAddedDesc' | 'DateAddedAsc' | 'StatusDesc' | 'StatusAsc';
export interface SuppressionPayload {
    statuses?: SuppressedStatus[] | ContactStatus[];
    search?: string;
    orderBy?: ContactsOrderBy;
    counts?: ContactCounts;
    activePage?: number;
    offset?: number;
    perPage?: number;
}

export type ContactCounts = {
    totalCount: number;
    bouncedCount: number;
    unsubscribedCount: number;
    abuseCount: number;
    inactiveCount: number;
    staleCount: number;
    transactionalCount: number;
};

export enum SuppressionsActionTypes {
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW',
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS',
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR',
    SET_PICKED_CONTACT_EMAIL = '@@suppressions/SET_PICKED_CONTACT_EMAIL',
    FETCH_CONTACTS = '@@suppressions/FETCH_CONTACTS',
    FETCH_CONTACTS_START = '@@suppressions/FETCH_CONTACTS_START',
    FETCH_CONTACTS_SUCCESS = '@@suppressions/FETCH_CONTACTS_SUCCESS',
    FETCH_CONTACTS_ERROR = '@@suppressions/FETCH_CONTACTS_ERROR',
    UPLOAD_FILES = '@@suppressions/UPLOAD_FILES',
    DELETE_SUPPRESSION = '@@suppressions/DELETE_SUPPRESSION',
    DELETE_SUPPRESSION_START = '@@suppressions/DELETE_SUPPRESSION_START',
    DELETE_SUPPRESSION_SUCCESS = '@@suppressions/DELETE_SUPPRESSION_SUCCESS',
    DELETE_SUPPRESSION_ERROR = '@@suppressions/DELETE_SUPPRESSION_ERROR',
    SEARCH_FOR_CONTACT = '@@suppressions/SEARCH_FOR_CONTACT',
    CREATE_SUPPRESSION = '@@suppressions/CREATE_SUPPRESSION',
    CREATE_SUPPRESSION_START = '@@suppressions/CREATE_SUPPRESSION_START',
    CREATE_SUPPRESSION_SUCCESS = '@@suppressions/CREATE_SUPPRESSION_SUCCESS',
    CREATE_SUPPRESSION_ERROR = '@@suppressions/CREATE_SUPPRESSION_ERROR',
    CREATE_SUPPRESSIONS = '@@suppressions/CREATE_SUPPRESSIONS',
    CREATE_SUPPRESSIONS_START = '@@suppressions/CREATE_SUPPRESSIONS_START',
    CREATE_SUPPRESSIONS_SUCCESS = '@@suppressions/CREATE_SUPPRESSIONS_SUCCESS',
    CREATE_SUPPRESSIONS_ERROR = '@@suppressions/CREATE_SUPPRESSIONS_ERROR',
    SEARCH_FOR_CONTACT_START = '@@suppressions/SEARCH_FOR_CONTACT_START',
    SEARCH_FOR_CONTACT_SUCCESS = '@@suppressions/SEARCH_FOR_CONTACT_SUCCESS',
    SEARCH_FOR_CONTACT_ERROR = '@@suppressions/SEARCH_FOR_CONTACT_ERROR',
    EXPORT_SUPPRESSIONS = '@@suppressions/EXPORT_SUPPRESSIONS',
    EXPORT_SUPPRESSIONS_START = '@@suppressions/EXPORT_SUPPRESSIONS_START',
    EXPORT_SUPPRESSIONS_SUCCESS = '@@suppressions/EXPORT_SUPPRESSIONS_SUCCESS',
    EXPORT_SUPPRESSIONS_ERROR = '@@suppressions/EXPORT_SUPPRESSIONS_ERROR',
    GET_CONTACTS_PATTERN = '@@suppressions/GET_CONTACTS_PATTERN',
    GET_CONTACTS_PATTERN_START = '@@suppressions/GET_CONTACTS_PATTERN_START',
    GET_CONTACTS_PATTERN_SUCCESS = '@@suppressions/GET_CONTACTS_PATTERN_SUCCESS',
    GET_CONTACTS_PATTERN_ERROR = '@@suppressions/GET_CONTACTS_PATTERN_ERROR',
    CLEAR_CONTACTS_PATTERN = '@@suppressions/CLEAR_CONTACTS_PATTERN',
    DELETE_SUPPRESSION_LIST = '@@suppressions/DELETE_SUPPRESSION_LIST',
    DELETE_SUPPRESSION_LIST_START = '@@suppressions/DELETE_SUPPRESSION_LIST_START',
    DELETE_SUPPRESSION_LIST_SUCCESS = '@@suppressions/DELETE_SUPPRESSION_LIST_SUCCESS',
    DELETE_SUPPRESSION_LIST_ERROR = '@@suppressions/DELETE_SUPPRESSION_LIST_ERROR',
    CLEAR_DATA = '@@suppressions/CLEAR_DATA',
    GET_CONTACTS_UNSUBSCRIBED = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED',
    GET_CONTACTS_UNSUBSCRIBED_SUCCESS = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED_SUCCESS',
    GET_CONTACTS_UNSUBSCRIBED_ERROR = '@@suppressions/GET_CONTACTS_UNSUBSCRIBED_ERROR',
    EXPORT_CONTACTS_UNSUBSCRIBED = '@@suppressions/EXPORT_CONTACTS_UNSUBSCRIBED',
}

export enum DateDropdownOptions {
    Calendar = 'calendar',
    Last7days = '7',
    Last14days = '14',
    Last30days = '30',
    Last3months = '90',
    Last6months = '180',
    Last12Months = '365',
    All = 'all',
}

export enum DropdownOptions {
    ViewDetails = 'viewdetails',
    CopyCampaign = 'copycampaign',
    Delete = 'delete',
    Pause = 'pause',
    Edit = 'edit',
    CancelSending = 'cancelsending',
    Reactivate = 'reactivate',
}

export interface SuppressionsContactsUnsubscribedPaginationInterface {
    from?: string;
    to?: string;
    statuses?: ContactStatus[];
    orderBy?: UnsubscribedContactsOrderBy;
    email?: string;
    activePage: number;
    perPage: number;
}

export type SuppressionsContactsUnsubscribedParams = {
    statuses: ContactStatus[];
    from?: string;
    to?: string;
    email?: string;
    orderBy?: UnsubscribedContactsOrderBy;
    limit: number;
    offset: number;
};

export interface SuppressionsUnsubscribedContactsStatusCounts {
    total: number;
    unsubscribed: number;
    abused: number;
}

export interface SuppressionsUnsubscribedContactsInterface {
    overview: UnsubscribedOverview;
    loadingUnsubscribedOverview: boolean;
    contactsList: ContactInfo[];
    pagination: SuppressionsContactsUnsubscribedPaginationInterface;
    loadingUnsubscribed: boolean;
    statusCount: SuppressionsUnsubscribedContactsStatusCounts;
    pickedContactEmail: string;
    unsubReasons: Array<ContactUnsubscribeReason>;
}

export interface GetContactsUnsubscribedOverviewAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW;
    payload: {
        from: string;
        to: string;
    };
}

export interface GetContactsUnsubscribedOverviewSuccessAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS;
    payload: SuppressionsUnsubscribedContactsInterface;
}

export interface GetContactsUnsubscribedOverviewErrorAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR;
}

export interface GetContactsUnsubscribedAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED;
    payload: SuppressionsContactsUnsubscribedParams;
}

export interface GetContactsUnsubscribedSuccessAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS;
    payload: SuppressionsUnsubscribedContactsInterface;
}

export interface GetContactsUnsubscribedErrorAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR;
}

export interface ExportContactsUnsubscribedAction extends Action {
    type: SuppressionsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED;
    payload: ExportContactsUnsubscribedParams;
}

export interface LoadSuppressionsAction extends Action {
    type: SuppressionsActionTypes.FETCH_CONTACTS;
    payload: SuppressionPayload;
}

export interface FindSuppressionsAction extends Action {
    type: SuppressionsActionTypes.SEARCH_FOR_CONTACT;
    payload: SuppressionPayload;
}

export interface StartLoadingSuppressionsAction extends Action {
    type: SuppressionsActionTypes.FETCH_CONTACTS_START;
}

export interface SuppressionsLoadingErrorAction extends Action {
    type: SuppressionsActionTypes.FETCH_CONTACTS_ERROR;
}

export interface SuppressionsLoadingSuccessAction extends Action {
    type: SuppressionsActionTypes.FETCH_CONTACTS_SUCCESS;
    payload: {
        contacts: any[];
    };
}

export interface AddSuppressionAction extends Action {
    type: SuppressionsActionTypes.CREATE_SUPPRESSION;
    payload: {
        emails: string[];
        status?: SuppressedStatus;
    };
}

export interface AddSuppressionSuccessAction extends Action {
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_SUCCESS;
    payload: {
        contacts: any[];
    };
}

export interface AddSuppressionStartAction extends Action {
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_START;
}

export interface AddSuppressionErrorAction extends Action {
    type: SuppressionsActionTypes.CREATE_SUPPRESSIONS_ERROR;
}

export interface DeleteSuppressionAction extends Action {
    type: SuppressionsActionTypes.DELETE_SUPPRESSION;
    payload: {
        emails: string[];
        selectedAll?: boolean;
    };
}

export interface DeleteSuppressionStartAction extends Action {
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_START;
}

export interface DeleteSuppressionErrorAction extends Action {
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_ERROR;
}

export interface DeleteSuppressionSuccessAction extends Action {
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_SUCCESS;
}

export interface ExportSuppressionAction extends Action {
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS;
    payload: ExportSuppressionsParams;
}

export interface ExportSuppressionStartAction extends Action {
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_START;
}

export interface ExportSuppressionErrorAction extends Action {
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_ERROR;
}

export interface ExportSuppressionSuccessAction extends Action {
    type: SuppressionsActionTypes.EXPORT_SUPPRESSIONS_SUCCESS;
}

export interface UploadContactsAction extends Action {
    type: SuppressionsActionTypes.UPLOAD_FILES;
    payload: {
        file: File;
        errorModal: JSX.Element;
    };
}

export interface GetContactsPatternAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN;
    payload: {
        fileName: string;
        errorModal: JSX.Element;
    };
}

export interface GetContactsPatternStartAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_START;
}

export interface GetContactsPatternSuccessAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_SUCCESS;
    payload: {
        contactsPattern: ContactPattern;
        fileName: string;
    };
}

export interface GetContactsPatternErrorAction extends Action {
    type: SuppressionsActionTypes.GET_CONTACTS_PATTERN_ERROR;
}

export interface ClearContactsPatternAction extends Action {
    type: SuppressionsActionTypes.CLEAR_CONTACTS_PATTERN;
}

export interface DeleteSuppresionsListAction extends Action {
    type: SuppressionsActionTypes.DELETE_SUPPRESSION_LIST;
    payload: {
        fileName: string;
    };
}

export interface CloseSupperssionsScreenAction extends Action {
    type: SuppressionsActionTypes.CLEAR_DATA;
}

export interface SetPickedContactEmailAction extends Action {
    type: SuppressionsActionTypes.SET_PICKED_CONTACT_EMAIL;
    payload: {
        pickedContactEmail: string;
    };
}

export interface SuppressionsState {
    contacts?: Contact[];
    statuses?: SuppressedStatus[] | ContactStatus[];
    contactsLoading: boolean;
    contactsUploading: boolean;
    selectedContact?: string;
    counts: ContactCounts;
    activePage: number;
    orderBy?: ContactsOrderBy;
    contactsPattern?: ContactPattern;
    fileName?: string;
    perPage: number;
    search: string;
    unsubscribedContacts: SuppressionsUnsubscribedContactsInterface;
}

export const ContactStatusMap = {
    '-2': 'Transactional',
    '-1': 'Engaged',
    '0': 'Active',
    '1': 'Bounced',
    '2': 'Unsubscribed',
    '3': 'Abuse',
    '4': 'Inactive',
    '5': 'Stale',
    '6': 'NotConfirmed',
};

export const ContactSourceMap = {
    '0': 'DeliveryApi',
    '1': 'ManualInput',
    '2': 'FileUpload',
    '3': 'WebForm',
    '4': 'ContactApi',
    '5': 'VerificationApi',
    '6': 'FileVerificationApi',
};

export const ConsentTrackingMap = {
    '0': 'Unknown',
    '1': 'Allow',
    '2': 'Deny',
};

export const defaultSuppressedStatuses = [SuppressedStatus.Complained, SuppressedStatus.Bounced, SuppressedStatus.Unsubscribed];
